import { useColorMode } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { ReactComponent as ArrowLeftIcon } from '../../../icons/arrow.left.svg';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';

interface StickyHeaderProps {
  title?: string | null;
  headerContainer?: ReactNode;
  leftContent?: ReactNode;
  centerContent?: ReactNode;
  rightContent?: ReactNode;
  alwaysShowTitle?: boolean;
  showTitle?: boolean;
  showBackButton?: boolean;
  transparent?: boolean;
  onClickBackButton?: (() => void) | null;
}

const StickyHeader = ({
  title,
  headerContainer = null,
  leftContent = null,
  centerContent = null,
  rightContent = null,
  alwaysShowTitle = false,
  showTitle,
  showBackButton,
  transparent = false,
  onClickBackButton = null
}: StickyHeaderProps): JSX.Element => {
  const [showHeader, setShowHeader] = useState<boolean>(false);
  const [lastScrollY, setLastScrollY] = useState<number>(0);
  const handleHistoryBack = useHandleHistoryBack();
  const { colorMode } = useColorMode();

  const handleScroll = (): void => {
    const currentScrollY = window.scrollY;

    if (currentScrollY > 20) {
      setShowHeader(true); // 스크롤 20px 이상이면 타이틀 표시
    } else {
      setShowHeader(false); // 스크롤 20px 이하이면 타이틀 숨김
    }

    setLastScrollY(currentScrollY);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ""
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastScrollY]);

  // 헤더 배경 클래스 결정
  const getHeaderBgClass = (): string => {
    if (transparent) return 'bg-transparent';
    if (!showHeader) return 'bg-transparent';
    return colorMode === 'dark' ? 'bg-[#0D0E12]' : 'bg-white';
  };

  // 헤더 텍스트 색상 클래스 결정
  const getHeaderTextClass = (): string => {
    return colorMode === 'dark' ? 'text-[#FAFAFA]' : 'text-[#222222]';
  };

  // 타이틀 색상 클래스 결정
  const getTitleColorClass = (): string => {
    if (alwaysShowTitle) {
      return colorMode === 'dark' ? 'text-[#FAFAFA]' : 'text-pure-black';
    }
    if (!showHeader && !alwaysShowTitle) return 'text-transparent';
    return colorMode === 'dark' ? 'text-[#FAFAFA]' : 'text-pure-black';
  };

  // 백 버튼 아이콘 색상 결정
  const getBackIconFill = (): string => {
    return colorMode === 'dark' ? '#FAFAFA' : '#222222';
  };

  return (
    <div style={{ maxWidth: 'var(--chakra-sizes-prose)' }}>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <header
        className={`
          fixed 
          max-w-prose 
          top-0 
          w-full 
          ${getHeaderBgClass()} 
          ${getHeaderTextClass()}
          flex 
          justify-between 
          items-center 
          pt-[calc(16px+env(safe-area-inset-top))] 
          pb-[15px] 
          pl-[calc(16px+env(safe-area-inset-left))] 
          pr-[calc(16px+env(safe-area-inset-right))] 
          z-[1000]
        `}
      >
        {headerContainer ? (
          headerContainer
        ) : (
          <>
            {(alwaysShowTitle || showTitle) && (
              <div className="absolute left-1/2 -translate-x-1/2 pointer-events-none">
                <div
                  className={`${getTitleColorClass()} font-bold text-[17px] leading-[20px] text-center`}
                >
                  {title}
                </div>
              </div>
            )}
            {/* Left Container */}
            <div className="flex-1 flex items-center">
              {showBackButton ? (
                <ArrowLeftIcon
                  fill={getBackIconFill()}
                  onClick={onClickBackButton ? onClickBackButton : handleHistoryBack}
                  className="cursor-pointer"
                />
              ) : (
                leftContent
              )}
            </div>

            {/* Center Container */}
            <div className="flex-[2] flex justify-center whitespace-nowrap">
              {!alwaysShowTitle && !showTitle ? centerContent : null}
            </div>

            {/* Right Container */}
            <div className="flex-1 flex justify-end">{rightContent}</div>
          </>
        )}
      </header>
    </div>
  );
};

export default StickyHeader;
